
import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Store, FileText } from "lucide-react";
import { validateToneContent, ToneValidationResult } from "@/utils/tone-validation";
import ToneValidationAlert from "../ToneValidationAlert";
import { RefundFormValues } from "@/hooks/use-refund-form";
import MessageFormatToggle from "./MessageFormatToggle";

interface BasicInfoFormProps {
  values: RefundFormValues;
  errors: { [key: string]: string };
  onChange: (field: keyof RefundFormValues, value: string) => void;
}

const BasicInfoForm = ({ values, errors, onChange }: BasicInfoFormProps) => {
  const [toneValidation, setToneValidation] = useState<ToneValidationResult | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  // Validate tone when issue content changes
  useEffect(() => {
    if (values.issue && values.issue.length > 20) {
      const validation = validateToneContent(values.issue);
      setToneValidation(validation);
      setShowValidation(!validation.isValid);
    } else {
      setToneValidation(null);
      setShowValidation(false);
    }
  }, [values.issue]);

  const handleFormatChange = (format: "email" | "chat") => {
    onChange('messageFormat', format);
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-5 w-5" />
            Request Details
          </CardTitle>
          <CardDescription className="mt-1">
            Tell us about the merchant and what went wrong
          </CardDescription>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Merchant Information */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Store className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="merchant" className="text-sm font-medium">
              Store/Company Name *
            </Label>
          </div>
          <Input
            id="merchant"
            placeholder="e.g., Amazon, Best Buy, Local Electronics Store"
            value={values.merchant}
            onChange={(e) => onChange('merchant', e.target.value)}
            className={errors.merchant ? "border-destructive" : ""}
          />
          {errors.merchant && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {errors.merchant}
            </div>
          )}
        </div>

        {/* Issue Description */}
        <div className="space-y-3">
          <Label htmlFor="issue" className="text-sm font-medium">
            What happened? *
          </Label>
          <Textarea
            id="issue"
            placeholder="Describe the issue with your purchase (e.g., product was defective, wrong item received, etc.)"
            value={values.issue}
            onChange={(e) => onChange('issue', e.target.value)}
            className={`min-h-[100px] resize-none ${errors.issue ? "border-destructive" : ""}`}
          />
          {errors.issue && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {errors.issue}
            </div>
          )}
          
          {showValidation && toneValidation && (
            <ToneValidationAlert validation={toneValidation} />
          )}
          
          <MessageFormatToggle 
            messageFormat={values.messageFormat || "email"}
            onFormatChange={handleFormatChange}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default BasicInfoForm;
